// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { UNKNOWN_SOURCE, type CourseHole } from './useCourseHoles';

/**
 * A hole's stored lengths arranged as tees down the side and sources across the top.
 *
 * The point of the grid is disagreement: the club's card, our own measurement of the same tee
 * and an OSM centreline sum all sit in one row, so a tee marked on the wrong box shows up as one
 * cell fifty metres off its neighbours rather than as a number nobody has anything to compare
 * with. Which of them a player is shown is decided server-side and is not this table's question.
 */

/**
 * Source columns in the order they are shown, best-attested first.
 *
 * Also the order a reader picks one number out of several by, on a screen with no room for a
 * column each — see {@link buildCourseCardTable}.
 */
export const LENGTH_SOURCE_ORDER = ['analysis', 'manual', 'golfisverige', 'golfingholiday', 'osm', UNKNOWN_SOURCE];

/** One row: the hole with no tee in mind, or the hole from one named tee. */
export type LengthTableRow = {
  /** Stable within one hole — the tee's id, or "hole" for the tee-less row. */
  key: string;
  /** The tee's name, or null for the hole's own row, which the caller names. */
  teeName: string | null;
  /** One entry per column in {@link LengthTable.sources}; null where that source has no number. */
  cells: (number | null)[];
};

export type LengthTable = {
  sources: string[];
  rows: LengthTableRow[];
};

/**
 * Only the sources this hole actually has a number from, in {@link LENGTH_SOURCE_ORDER}.
 *
 * An unrecognised source is kept rather than dropped — a length nobody expected is still a
 * length somebody stored, and hiding the column would make the row silently incomplete — and
 * sorts after the known ones, alphabetically so the order does not depend on arrival.
 */
const sourcesOf = (hole: CourseHole): string[] => {
  const present = new Set<string>([
    ...hole.lengths.map((length) => length.source),
    ...hole.teeLengths.map((length) => length.source),
  ]);
  return [...present].sort((a, b) => {
    const rankA = LENGTH_SOURCE_ORDER.indexOf(a);
    const rankB = LENGTH_SOURCE_ORDER.indexOf(b);
    if (rankA === rankB) return a.localeCompare(b);
    if (rankA < 0) return 1;
    if (rankB < 0) return -1;
    return rankA - rankB;
  });
};

const cellsFrom = (
  sources: readonly string[],
  lengths: readonly { source: string; lengthMeters: number }[]
): (number | null)[] =>
  sources.map(
    (source) => lengths.find((length) => length.source === source)?.lengthMeters ?? null
  );

/**
 * The grid for one hole, or null when the hole has no stored length at all.
 *
 * Null rather than an empty table: a heading over nothing reads as "measured, and it came to
 * nothing", where the truth is that nobody has measured this hole yet.
 *
 * Tees keep the order they arrived in — the catalogue sorts them by name — and a tee appears
 * once however many sources measured it. Two rows for the same tee and the same source cannot
 * both be stored, so the first one wins without a rule worth stating.
 */
export const buildHoleLengthTable = (hole: CourseHole | null): LengthTable | null => {
  if (!hole) return null;
  const sources = sourcesOf(hole);
  if (sources.length === 0) return null;

  const teeIds: string[] = [];
  for (const length of hole.teeLengths) {
    if (!teeIds.includes(length.teeId)) teeIds.push(length.teeId);
  }

  const rows: LengthTableRow[] = [];
  if (hole.lengths.length > 0) {
    rows.push({ key: 'hole', teeName: null, cells: cellsFrom(sources, hole.lengths) });
  }
  for (const teeId of teeIds) {
    const forTee = hole.teeLengths.filter((length) => length.teeId === teeId);
    rows.push({ key: teeId, teeName: forTee[0].teeName, cells: cellsFrom(sources, forTee) });
  }

  return { sources, rows };
};
